import * as tokens from '@maas-components/design-tokens';
import { Theme } from '@material-ui/core/styles';

const createDefault = (theme: Theme) => {
    theme.shadows[1] = tokens.shadow.dp1;
    theme.shadows[2] = tokens.shadow.dp2;
    theme.shadows[3] = tokens.shadow.dp3;
    theme.shadows[4] = tokens.shadow.dp4;

    theme.shape.borderRadius = tokens.radii.semiRounded;

    theme.palette.divider = tokens.colors.neutral.c300;

    theme.palette.common = {
        black: tokens.colors.black,
        white: tokens.colors.white,
    };

    theme.palette.text = {
        ...theme.palette.text,
        primary: tokens.colors.neutral.c600,
        secondary: tokens.colors.neutral.c500,
        disabled: tokens.colors.neutral.c400,
        hint: tokens.colors.neutral.c400,
    };

    theme.palette.background = {
        default: tokens.colors.neutral.c50,
        paper: tokens.colors.neutral.c100,
    };

    theme.palette.primary = {
        ...theme.palette.primary,
        light: tokens.colors.blue.c90,
        main: tokens.colors.blue.c100,
        dark: tokens.colors.blue.c110,
    };

    theme.palette.secondary = {
        ...theme.palette.secondary,
        light: tokens.colors.neutral.c400,
        main: tokens.colors.neutral.c500,
        dark: tokens.colors.neutral.c600,
    };

    theme.palette.error = {
        ...theme.palette.error,
        light: tokens.colors.red.c90,
        main: tokens.colors.red.c100,
        dark: tokens.colors.red.c110,
    };

    theme.palette.success = {
        ...theme.palette.success,
        light: tokens.colors.green.c90,
        main: tokens.colors.green.c100,
        dark: tokens.colors.green.c110,
        contrastText: tokens.colors.neutral.c100,
    };

    theme.palette.info = {
        ...theme.palette.info,
        light: tokens.colors.violet.c90,
        main: tokens.colors.violet.c100,
        dark: tokens.colors.violet.c110,
    };

    theme.palette.warning = {
        ...theme.palette.warning,
        light: tokens.colors.yellow.c90,
        main: tokens.colors.yellow.c100,
        dark: tokens.colors.yellow.c110,
    };

    theme.palette.grey = {
        ...theme.palette.grey,
        50: tokens.colors.neutral.c50,
        100: tokens.colors.neutral.c100,
        200: tokens.colors.neutral.c200,
        300: tokens.colors.neutral.c300,
        400: tokens.colors.neutral.c400,
        500: tokens.colors.neutral.c500,
        600: tokens.colors.neutral.c600,
        700: tokens.colors.neutral.c700,
        800: tokens.colors.neutral.c800,
        900: tokens.colors.neutral.c900,
    };

    theme.typography.fontFamily = tokens.fontFamily.magalu;
    theme.typography.fontSize = 14;

    theme.typography.fontWeightBold = tokens.fontWeight.bold;
    theme.typography.fontWeightMedium = tokens.fontWeight.medium;
    theme.typography.fontWeightRegular = tokens.fontWeight.regular;

    theme.typography.h1 = {
        fontWeight: tokens.fontWeight.bold,
        fontSize: theme.typography.pxToRem(tokens.fontSize.xxxlarge),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.xxxlarge),
        letterSpacing: 0,
    };

    theme.typography.h2 = {
        fontWeight: tokens.fontWeight.bold,
        fontSize: theme.typography.pxToRem(tokens.fontSize.xxlarge),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.xxlarge),
        letterSpacing: 0,
    };

    theme.typography.h3 = {
        fontWeight: tokens.fontWeight.bold,
        fontSize: theme.typography.pxToRem(tokens.fontSize.xlarge),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.xlarge),
        letterSpacing: 0,
    };

    theme.typography.h4 = {
        fontWeight: tokens.fontWeight.bold,
        fontSize: theme.typography.pxToRem(tokens.fontSize.large),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.large),
        letterSpacing: 0,
    };

    theme.typography.h5 = {
        fontWeight: tokens.fontWeight.bold,
        fontSize: theme.typography.pxToRem(tokens.fontSize.medium),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.medium),
        letterSpacing: 0,
    };

    theme.typography.h6 = {
        fontWeight: tokens.fontWeight.bold,
        fontSize: theme.typography.pxToRem(tokens.fontSize.small),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.small),
        letterSpacing: 0,
    };

    theme.typography.subtitle1 = {
        fontWeight: tokens.fontWeight.medium,
        fontSize: theme.typography.pxToRem(tokens.fontSize.default),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.medium),
        letterSpacing: 0,
    };

    theme.typography.subtitle2 = {
        fontWeight: tokens.fontWeight.bold,
        fontSize: theme.typography.pxToRem(tokens.fontSize.small),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.small),
        letterSpacing: 0,
    };

    theme.typography.body1 = {
        fontWeight: tokens.fontWeight.regular,
        fontSize: theme.typography.pxToRem(tokens.fontSize.default),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.small),
        letterSpacing: 0,
    };

    theme.typography.body2 = {
        fontWeight: tokens.fontWeight.regular,
        fontSize: theme.typography.pxToRem(tokens.fontSize.small),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.xsmall),
        letterSpacing: 0,
    };

    theme.typography.button = {
        fontWeight: tokens.fontWeight.medium,
        fontSize: theme.typography.pxToRem(tokens.fontSize.default),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.small),
        letterSpacing: 0,
        textTransform: 'initial',
    };

    theme.typography.caption = {
        fontWeight: tokens.fontWeight.medium,
        fontSize: theme.typography.pxToRem(tokens.fontSize.xsmall),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.xsmall),
        letterSpacing: 0,
    };

    theme.typography.overline = {
        fontWeight: tokens.fontWeight.medium,
        fontSize: theme.typography.pxToRem(tokens.fontSize.default),
        lineHeight: theme.typography.pxToRem(tokens.lineHeight.small),
        letterSpacing: 0,
    };

    return theme;
};

export default createDefault;
